import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function InnerForm({ customerId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchSetupIntent();
  }, [customerId]);

  const fetchSetupIntent = async () => {
    try {
      const response = await axios.get(`/payments/setup-intent?customer_id=${customerId}`);
      setClientSecret(response.data.clientSecret);
    } catch (err) {
      setMsg('Failed to initialize. Please try again.');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');

    if (!stripe || !elements) {
      return;
    }

    const card = elements.getElement(CardElement);

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card
        }
      });

      if (result.error) {
        setMsg(result.error.message);
        setSaving(false);
        return;
      }

      await axios.post('/payments/attach-default', {
        customer_id: customerId,
        setup_intent_id: result.setupIntent.id
      });

      setMsg('✅ Card saved successfully!');
      elements.getElement(CardElement).clear();
      
      // Refresh setup intent for next card
      await fetchSetupIntent();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} data-testid="save-card-form">
      <div className="border border-gray-300 rounded-lg p-4 mb-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={!stripe || saving}
        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="save-card-button"
      >
        {saving ? 'Saving...' : 'Save Card'}
      </button>
      
      {msg && (
        <div className={`mt-3 text-sm ${
          msg.includes('✅') ? 'text-green-600' : 'text-red-600'
        }`} data-testid="card-message">
          {msg}
        </div>
      )}
    </form>
  );
}

const SaveCardForm = ({ customerId }) => {
  return (
    <Elements stripe={stripePromise}>
      <InnerForm customerId={customerId} />
    </Elements>
  );
};

export default SaveCardForm;
